import type { AppearanceMode } from "../JobCard";

import { Pressable } from "../../../../mads";
import { Bookmark } from "../../../../mads/icons";
import { useI18n } from "../../../../shared/I18n";

import { colors, spacing } from "@indeed/ifl-tokens/react-native";
import { type GestureResponderEvent, StyleSheet } from "react-native";

export type SaveButtonProps = {
  appearanceMode: AppearanceMode;
  isSavedJob: boolean;
  isSaveJobMutationLoading: boolean;
  onPressSave: () => void;
  jobTitle?: string;
  testID?: string;
};

export function SaveButton({
  appearanceMode,
  isSavedJob,
  isSaveJobMutationLoading,
  onPressSave,
  jobTitle,
  testID,
}: SaveButtonProps) {
  const i18n = useI18n();

  const fillColor =
    appearanceMode === "default" ? colors.foregroundStrong : colors.foregroundPrimary;

  const strokeColor = colors.foregroundStrong;

  let accessibilityLabel: string;
  if (isSavedJob) {
    accessibilityLabel = jobTitle
      ? i18n.gettext("Remove saved job, {0}", [jobTitle])
      : i18n.gettext("Remove saved job");
  } else {
    accessibilityLabel = jobTitle
      ? i18n.gettext("Save job, {0}", [jobTitle])
      : i18n.gettext("Save job");
  }

  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      sx={({ pressed }) => [styles.saveButton, pressed && styles.saveButtonPressed]}
      onPress={(event: GestureResponderEvent) => {
        event.stopPropagation();
        event.preventDefault();
        onPressSave();
      }}
      accessibilityRole="button"
      disabled={isSaveJobMutationLoading}
      role="button"
      accessibilityHint={
        jobTitle
          ? i18n.gettext("{0} group. Tap to toggle save status", [jobTitle])
          : i18n.gettext("Job actions group. Tap to toggle save status")
      }
      testID={testID}
      // On iOS, the Bookmark icon's SVG emits role="img" even with accessible={false},
      // causing it to register in the accessibility tree and preventing Appium from
      // finding this Pressable via testID. Setting accessible={true} forces
      // correct grouping. Filed JSMA-4480 to fix the root cause in Icon.tsx.
      accessible={true}
    >
      <Bookmark
        variant={isSavedJob ? "filled" : "stroke"}
        fill={isSavedJob ? fillColor : strokeColor}
      />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  saveButton: {
    padding: 4,
    borderRadius: spacing.s6,
    justifyContent: "center",
    alignItems: "center",
  },
  saveButtonPressed: {
    backgroundColor: colors.backgroundStrong,
  },
});
